import React, { useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { DownloadSimple, ListMagnifyingGlass, FileText, Fingerprint } from "@phosphor-icons/react";
import api, { API } from "../lib/api";
import { ScoreGauge } from "../components/ScoreGauge";
import { FindingCard } from "../components/FindingCard";
import { useI18n } from "../lib/i18n";

export default function Result() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const findingsRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["analysis", id],
    queryFn: async () => (await api.get(`/analyses/${id}`)).data,
  });

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/analyses/${id}/report`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `LEXADM_${(data?.analysis?.filename || "documento").replace(/\.[^.]+$/, "")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Falha ao gerar o relatório. Tente novamente.");
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading || !data)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="result-loading">
        {t("common.loading")}
      </div>
    );

  const { analysis, findings } = data;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* Score column */}
        <div className="lg:col-span-5 lg:sticky lg:top-10 animate-fade-up">
          <div className="bg-white border border-slate-200 rounded-lg p-8 flex flex-col items-center text-center">
            <p className="label-caps">{t("res.title")}</p>
            <div className="my-4">
              <ScoreGauge score={analysis.score} level={analysis.level} />
            </div>
            <p className="text-sm font-semibold text-navy" data-testid="findings-count">
              {analysis.findings_count} {t("res.found")}
            </p>

            <div className="w-full mt-5 pt-5 border-t border-slate-100 text-left space-y-3">
              <div className="flex items-start gap-2.5">
                <FileText size={18} className="text-slate-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="label-caps">{t("hist.document")}</div>
                  <div className="text-sm font-medium text-navy break-words" data-testid="result-filename">
                    {analysis.filename}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Fingerprint size={18} className="text-slate-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="label-caps">SHA-256</div>
                  <div className="text-[11px] font-mono text-slate-500 break-all">{analysis.sha256}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <button
              onClick={() => findingsRef.current?.scrollIntoView({ behavior: "smooth" })}
              data-testid="view-findings-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-navy text-white text-sm font-semibold rounded-md py-3 hover:bg-black transition-colors duration-200"
            >
              <ListMagnifyingGlass size={18} weight="bold" />
              {t("res.viewFindings")}
            </button>
            <button
              onClick={download}
              disabled={downloading}
              data-testid="download-report-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-white text-sm font-semibold rounded-md py-3 hover:brightness-95 transition-all duration-200 disabled:opacity-60"
            >
              <DownloadSimple size={18} weight="bold" />
              {downloading ? t("common.loading") : t("res.download")}
            </button>
          </div>
        </div>

        {/* Summary + findings */}
        <div className="lg:col-span-7 animate-fade-up" style={{ animationDelay: "80ms" }}>
          <div className="bg-white border border-slate-200 rounded-lg p-7">
            <h2 className="font-display font-bold text-2xl text-navy">
              {analysis.filename}
            </h2>
            <p className="text-base text-slate-600 leading-relaxed mt-3">{analysis.summary}</p>
          </div>

          <div ref={findingsRef} className="mt-8 scroll-mt-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="label-caps">{t("res.findings")}</h3>
              <span className="text-xs text-slate-400">{findings.length}</span>
            </div>
            {findings.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-md px-6 py-10 text-center text-sm text-slate-400">
                {t("res.none")}
              </div>
            ) : (
              <div className="space-y-4">
                {findings.map((f, i) => (
                  <FindingCard
                    key={f.id || i}
                    finding={f}
                    index={i}
                    onViewDoc={() => navigate(`/viewer/${id}?f=${i}`)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
